import { useOnboarding } from "@onboardjs/react";
import { useMutation, useQuery } from "convex/react";
import { CheckCircle2, Loader2, Mail } from "lucide-react";
import { useState } from "react";
import { api } from "../../../convex/_generated/api";
import { Button } from "../../components/ui/button";

type EmailProvider = "gmail" | "ms-outlook";

export function ConnectEmailStep() {
	const { next } = useOnboarding();
	const gmailStatus = useQuery(
		api.integrations.connections.getConnectionStatus,
		{
			provider: "gmail",
		},
	);
	const outlookStatus = useQuery(
		api.integrations.connections.getConnectionStatus,
		{ provider: "ms-outlook" },
	);

	const beginOAuth = useMutation(api.integrations.connections.beginOAuth);
	const startPersonExtraction = useMutation(
		api.codex.mutations.startPersonExtraction,
	);

	const [connecting, setConnecting] = useState<EmailProvider | null>(null);

	const isGmailConnected = gmailStatus?.status === "connected";
	const isOutlookConnected = outlookStatus?.status === "connected";
	const isAnyConnected = isGmailConnected || isOutlookConnected;

	const handleConnect = async (provider: EmailProvider) => {
		setConnecting(provider);
		try {
			const callbackPath =
				provider === "gmail"
					? "/setup/callback/google"
					: "/setup/callback/outlook";
			const redirectUri = `${window.location.origin}${callbackPath}`;
			const { authorizationUrl } = await beginOAuth({
				provider,
				redirectUri,
			});
			window.location.href = authorizationUrl;
		} catch {
			setConnecting(null);
		}
	};

	const handleContinue = async () => {
		const provider = isOutlookConnected ? "ms-outlook" : "gmail";
		try {
			await startPersonExtraction({
				provider: provider as "gmail" | "ms-outlook",
			});
		} catch {
			// Non-blocking — extraction will be retried or manual entry used
		}
		await next();
	};

	const allProviders: Array<{
		id: EmailProvider;
		name: string;
		description: string;
		isConnected: boolean;
		email: string | undefined;
	}> = [
		{
			id: "ms-outlook",
			name: "Microsoft Outlook",
			description: "Connect your Outlook account for email management",
			isConnected: isOutlookConnected,
			email: outlookStatus?.accountEmail ?? undefined,
		},
		{
			id: "gmail",
			name: "Gmail",
			description: "Connect your Gmail account for email management",
			isConnected: isGmailConnected,
			email: gmailStatus?.accountEmail ?? undefined,
		},
	];

	// Better Auth auto-connect already populates oauthConnections for the
	// sign-in provider; show both anyway, manual connect is the fallback.
	const providers = allProviders;

	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col gap-3">
				{providers.map((provider) => (
					<div
						key={provider.id}
						className="flex items-center justify-between rounded-lg border p-4"
					>
						<div className="flex items-center gap-3">
							<div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
								<Mail className="h-5 w-5" />
							</div>
							<div>
								<p className="font-medium">{provider.name}</p>
								{provider.isConnected ? (
									<p className="text-success text-sm">{provider.email}</p>
								) : (
									<p className="text-sm text-muted-foreground">
										{provider.description}
									</p>
								)}
							</div>
						</div>
						<div>
							{provider.isConnected ? (
								<div className="flex items-center gap-2 text-success">
									<CheckCircle2 className="h-4 w-4" />
									<span className="text-sm font-medium">Connected</span>
								</div>
							) : (
								<Button
									variant="outline"
									size="sm"
									onClick={() => handleConnect(provider.id)}
									disabled={connecting !== null}
								>
									{connecting === provider.id && (
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									)}
									Connect
								</Button>
							)}
						</div>
					</div>
				))}
			</div>

			<div className="flex justify-end pt-2">
				<Button onClick={handleContinue} disabled={!isAnyConnected}>
					Continue
				</Button>
			</div>
		</div>
	);
}
